import { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import queryString from 'query-string';
import { LocksListTypes, Lock } from './types';
import useQueryState from '../table/hooks/use-query-state';
import usePrevious from '../../hooks/use-previous';
import useGetList from '../../hooks/use-get-list';
import useFilters from '../table/hooks/use-filters';
import formatLocksList from './helpers/format-locks-list';
import { SelectItemProps } from '../select/types';
import { reasons } from './variables/lists';
import { Filters, SchemaFilterType } from '../table/types';
import IconLoader from '../icons/loader';
import Table from '../table/table';
import DateInputRange from '../date-input/date-input-range';
import { PickerPosition } from '../date-input/types';

import cn from './styles.module.css';

export default function SecurityLocks({
  locks,
  pagination,
  takeCount,
  users,
  schema,
  getList,
  filtersSchema,
  isFetching,
  isLoading,
  columns,
  columnsLabels,
  userId,
  tableTopClassName = '',
  expirationDatesPickerPosition = PickerPosition.LEFT,
  withPickerTitles = false,
}: LocksListTypes) {
  const [createdAt, setCreatedAt] = useState<string[]>([]);
  const [expiresAt, setExpiresAt] = useState<string[]>([]);

  const { filters, sort, onSortChange, onFilterChange } = useQueryState(schema);

  const prevFilters = usePrevious(filters);

  const { search } = useLocation();

  const { getListAction, setPage, onFilter, errorList } = useGetList<Lock>({
    getList,
    onFilterChange,
    onSortChange,
    schema,
  });

  const { filtersComponent, setValues, handleSubmit } = useFilters({
    filtersSchema,
    filters,
    onFilterChange: onFilter,
  });

  const formattedData = useMemo(() => {
    const data = formatLocksList(locks as Lock[], users);

    return data;
  }, [locks, sort, filters, users]);

  useEffect(() => {
    setValues(
      'createdByEmployeeId',
      users?.map((item) => ({
        name: `${item?.profile?.firstName} ${item?.profile?.lastName}`,
        value: item.id,
      })) as SelectItemProps[],
    );

    setValues('reason', reasons);
  }, [users]);

  useEffect(() => {
    if (takeCount && takeCount !== 0 && filters && !locks && search) {
      const nextFilters: Filters = {
        ...filters,
        take: {
          value: [takeCount.toString()],
          type: SchemaFilterType.STRING,
        },
      };

      if (userId) {
        nextFilters.userId = {
          value: [userId],
          type: SchemaFilterType.STRING,
        };
      }

      if (search) {
        const parsed = queryString.parse(search);

        if (parsed.createdAtFrom) {
          const createdAtDates: string[] = [
            new Date(parsed.createdAtFrom as string).toISOString(),
          ];

          if (parsed.createdAtTo) {
            createdAtDates.push(
              new Date(parsed.createdAtTo as string).toISOString(),
            );
          }

          setCreatedAt(createdAtDates);

          nextFilters.createdAt = {
            value: createdAtDates,
            type: SchemaFilterType.DATE_RANGE,
          };
        }

        if (parsed.expiresAtFrom) {
          const expiresAtDates: string[] = [
            new Date(parsed.expiresAtFrom as string).toISOString(),
          ];

          if (parsed.expiresAtTo) {
            expiresAtDates.push(
              new Date(parsed.expiresAtTo as string).toISOString(),
            );
          }

          setExpiresAt(expiresAtDates);

          nextFilters.expiresAt = {
            value: expiresAtDates,
            type: SchemaFilterType.DATE_RANGE,
          };
        }
      }

      getListAction({
        filters: nextFilters,
      });
    }
  }, [takeCount, search, locks, filters, userId]);

  useEffect(() => {
    if (
      takeCount &&
      takeCount !== 0 &&
      prevFilters &&
      !Object.keys(prevFilters).length &&
      filters &&
      !locks &&
      !search
    ) {
      const nextFilters: Filters = {
        ...filters,
        take: {
          value: [takeCount.toString()],
          type: SchemaFilterType.STRING,
        },
      };

      if (userId) {
        nextFilters.userId = {
          value: [userId],
          type: SchemaFilterType.STRING,
        };
      }

      getListAction({
        filters: nextFilters,
      });
    }
  }, [filters, prevFilters, locks, takeCount, search, userId]);

  let content;

  if (isLoading) {
    content = (
      <div className={cn.locksLoader}>
        <IconLoader />
      </div>
    );
  } else {
    content = (
      <div className={cn.securityLocksTableWrapper}>
        <Table
          columns={columns}
          data={formattedData || []}
          onSort={() => {}}
          filters={filters}
          sort={sort}
          columnsLabels={columnsLabels}
          tableTopClassName={`${cn.locksTableTop} ${tableTopClassName}`}
          onFilterChange={(filterValues) => {
            const nextFilters: Filters = {
              ...filterValues,
              take: {
                value: [(takeCount as number)?.toString()],
                type: SchemaFilterType.STRING,
              },
            };

            if (userId) {
              nextFilters.userId = {
                value: [userId],
                type: SchemaFilterType.STRING,
              };
            }

            if (createdAt?.length) {
              const createdAtDates: string[] = [
                new Date(createdAt[0]).toISOString(),
              ];

              if (createdAt?.length > 1) {
                createdAtDates.push(new Date(createdAt[1]).toISOString());
              }

              nextFilters.createdAt = {
                value: createdAtDates,
                type: SchemaFilterType.DATE_RANGE,
              };
            }

            if (expiresAt?.length) {
              const expiresAtDates: string[] = [
                new Date(expiresAt[0]).toISOString(),
              ];

              if (expiresAt?.length > 1) {
                expiresAtDates.push(new Date(expiresAt[1]).toISOString());
              }

              nextFilters.expiresAt = {
                value: expiresAtDates,
                type: SchemaFilterType.DATE_RANGE,
              };
            }

            onFilter({
              ...nextFilters,
            });
          }}
          topContent={
            <div className={cn.securityDatesControls}>
              <DateInputRange
                values={createdAt}
                placeholder="Select Creation dates"
                dateInputTitle="Creation Date"
                className={`${cn.securityDatesPicker} ${
                  !createdAt.length ? cn.empty : ''
                }`}
                title="Creation Date"
                onChangeRange={(dates) => {
                  setCreatedAt(
                    dates?.map((item) => new Date(item).toISOString()),
                  );

                  const nextFilters: Filters = {
                    ...filters,
                    take: {
                      value: [(takeCount as number)?.toString()],
                      type: SchemaFilterType.STRING,
                    },
                  };

                  if (userId) {
                    nextFilters.userId = {
                      value: [userId],
                      type: SchemaFilterType.STRING,
                    };
                  }

                  const datesValues: string[] = [
                    new Date(dates[0]).toISOString(),
                  ];

                  if (dates?.length > 1) {
                    datesValues.push(new Date(dates[1]).toISOString());
                  }

                  nextFilters.createdAt = {
                    value: datesValues,
                    type: SchemaFilterType.DATE_RANGE,
                  };

                  onFilter({
                    ...nextFilters,
                  });
                }}
                onReset={() => {
                  setCreatedAt([]);

                  const nextFilters: Filters = {
                    ...filters,
                    take: {
                      value: [(takeCount as number)?.toString()],
                      type: SchemaFilterType.STRING,
                    },
                  };

                  if (userId) {
                    nextFilters.userId = {
                      value: [userId],
                      type: SchemaFilterType.STRING,
                    };
                  }

                  if (nextFilters.createdAt) {
                    delete nextFilters.createdAt;
                  }

                  onFilter({
                    ...nextFilters,
                  });
                }}
                withTitle={withPickerTitles}
                withoutCustomSelector
                withResetClose
              />
              <DateInputRange
                values={expiresAt}
                placeholder="Select Expiration dates"
                dateInputTitle="Expiration Date"
                className={`${cn.securityDatesPicker} ${
                  !expiresAt.length ? cn.empty : ''
                }`}
                title="Expiration dates"
                pickerPisition={expirationDatesPickerPosition}
                onChangeRange={(dates) => {
                  setExpiresAt(
                    dates?.map((item) => new Date(item).toISOString()),
                  );

                  const nextFilters: Filters = {
                    ...filters,
                    take: {
                      value: [(takeCount as number)?.toString()],
                      type: SchemaFilterType.STRING,
                    },
                  };

                  if (userId) {
                    nextFilters.userId = {
                      value: [userId],
                      type: SchemaFilterType.STRING,
                    };
                  }

                  const datesValues: string[] = [
                    new Date(dates[0]).toISOString(),
                  ];

                  if (dates?.length > 1) {
                    datesValues.push(new Date(dates[1]).toISOString());
                  }

                  nextFilters.expiresAt = {
                    value: datesValues,
                    type: SchemaFilterType.DATE_RANGE,
                  };

                  onFilter({
                    ...nextFilters,
                  });
                }}
                onReset={() => {
                  setExpiresAt([]);

                  const nextFilters: Filters = {
                    ...filters,
                    take: {
                      value: [(takeCount as number)?.toString()],
                      type: SchemaFilterType.STRING,
                    },
                  };

                  if (userId) {
                    nextFilters.userId = {
                      value: [userId],
                      type: SchemaFilterType.STRING,
                    };
                  }

                  if (nextFilters.expiresAt) {
                    delete nextFilters.expiresAt;
                  }

                  onFilter({
                    ...nextFilters,
                  });
                }}
                withTitle={withPickerTitles}
                withoutCustomSelector
                withResetClose
              />
            </div>
          }
          filtersComponent={filtersComponent}
          handleSubmit={handleSubmit}
          isLoading={isLoading || isFetching || !takeCount}
          take={takeCount?.toString()}
          setPage={(data) => {
            const filterValues: Filters = {
              ...filters,
              take: {
                value: [(takeCount as number)?.toString()],
                type: SchemaFilterType.STRING,
              },
            };

            if (userId) {
              filterValues.userId = {
                value: [userId],
                type: SchemaFilterType.STRING,
              };
            }

            if (createdAt?.length) {
              const createdAtDates: string[] = [
                new Date(createdAt[0]).toISOString(),
              ];

              if (createdAt?.length > 1) {
                createdAtDates.push(new Date(createdAt[1]).toISOString());
              }

              filterValues.createdAt = {
                value: createdAtDates,
                type: SchemaFilterType.DATE_RANGE,
              };
            }

            if (expiresAt?.length) {
              const expiresAtDates: string[] = [
                new Date(expiresAt[0]).toISOString(),
              ];

              if (expiresAt?.length > 1) {
                expiresAtDates.push(new Date(expiresAt[1]).toISOString());
              }

              filterValues.expiresAt = {
                value: expiresAtDates,
                type: SchemaFilterType.DATE_RANGE,
              };
            }

            setPage({ ...data, filters: filterValues });
          }}
          pagination={pagination}
          error={errorList}
          withVerticalScroll
          withScroll
          clickable
        />
      </div>
    );
  }

  return content;
}
